import { useSessionStage } from '@/hooks/useSessionStage';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef } from 'react';
import { V2_ACTIONS, V2_REWRITE_SUBMENU } from './selection-ai-actions.config';
import {
  isSelectionCheckGuidelinesAllowed,
  resolveSelectionActionTarget,
} from './selection-ai-actions.utils';

export const useSelectionAiMenuShownTracking = ({
  editor,
  getTargetRange,
  subscribeToSelectionUpdate = false,
  suggestionCount: suggestionCountOverride,
}: {
  editor: Editor;
  getTargetRange?: () => { from: number; to: number } | null;
  /** For hosts that render a different action list than `SelectionAiActions`. */
  suggestionCount?: number;
  /**
   * Hosts without a BubbleMenu `shouldShow` (e.g. the section kebab) subscribe
   * here. The text bubble menu tracks from `onShow` / `onHide` instead.
   */
  subscribeToSelectionUpdate?: boolean;
}) => {
  const sessionId = useCurrentSessionId();
  const sessionStage = useSessionStage();
  const visibleV2Actions = V2_ACTIONS.filter(
    (action) => action.id !== 'check_guidelines' || isSelectionCheckGuidelinesAllowed()
  );
  // Selection AI rows only: rewrite submenu + visible V2 rows + Edit with AI.
  // The bubble-menu "More options" editor actions are omitted on purpose.
  const suggestionCount =
    suggestionCountOverride ?? V2_REWRITE_SUBMENU.length + visibleV2Actions.length + 1;
  // Dedupes the repeated predicate runs of one menu opening — must be cleared
  // on hide, or re-selecting the same range never counts again.
  const lastMenuShownSelectionKeyRef = useRef<string | null>(null);

  const trackMenuShown = useCallback(() => {
    const target = resolveSelectionActionTarget(editor, getTargetRange);
    if (!target) {
      lastMenuShownSelectionKeyRef.current = null;
      return;
    }
    const selectionKey = `${target.from}:${target.to}`;
    if (lastMenuShownSelectionKeyRef.current === selectionKey) return;
    lastMenuShownSelectionKeyRef.current = selectionKey;
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        action: ButtonAction.AskHeidiSelectionMenuShown,
        label: 'selection_ai_actions',
        click_source: 'selection_menu',
        selection_length: target.text.length,
        suggestion_count: suggestionCount,
        session_id: sessionId || undefined,
        session_stage: sessionId ? sessionStage : undefined,
      },
      options: { amplitude: false },
    });
  }, [editor, getTargetRange, sessionId, sessionStage, suggestionCount]);

  const trackMenuHidden = useCallback(() => {
    lastMenuShownSelectionKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (!subscribeToSelectionUpdate) return;

    trackMenuShown();
    editor.on('selectionUpdate', trackMenuShown);
    return () => {
      editor.off('selectionUpdate', trackMenuShown);
    };
  }, [editor, subscribeToSelectionUpdate, trackMenuShown]);

  return { trackMenuShown, trackMenuHidden, visibleV2Actions };
};
